import React, { createContext, FC, ReactNode, useContext, useMemo } from "react";
import invariant from "tiny-invariant";
import { NextBestActionType } from "../../../../../projection/nextBestActionDefinition/nextBestActionDefinition";
import { NextBestActionItem } from "../../../components/nextBestActionItem/NextBestActionItem";

type NextBestActionItemComponent = NextBestActionItem<NextBestActionType>;

type NextBestActionItems = Record<NextBestActionType, NextBestActionItemComponent>;

const NextBestActionItemContext = createContext<NextBestActionItems>(null as unknown as NextBestActionItems);

interface NextBestActionItemProviderProps {
  readonly nextBestActionItems: NextBestActionItems;
  readonly children: ReactNode;
}

const NextBestActionItemProvider: FC<NextBestActionItemProviderProps> = ({ nextBestActionItems, children }) => (
  <NextBestActionItemContext.Provider value={nextBestActionItems}>{children}</NextBestActionItemContext.Provider>
);

interface UseNextBestActionItemFunctionArgs {
  readonly type: NextBestActionType;
}

interface UseNextBestActionItemFunction {
  (args: UseNextBestActionItemFunctionArgs): NextBestActionItemComponent;
}

const useNextBestActionItem: UseNextBestActionItemFunction = ({ type }) => {
  const items = useContext<NextBestActionItems>(NextBestActionItemContext);
  const item = useMemo(() => items[type], [items, type]);

  invariant(
    items,
    "You are trying to use the useNextBestActionItem hook without wrapping your app with the <NextBestActionItemProvider>.",
  );

  invariant(item, `The provided NextBestActionType (${type}) is NOT SUPPORTED`);

  return item!;
};

export type { NextBestActionItems };
export { useNextBestActionItem, NextBestActionItemProvider };
